<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb :title="page.title" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

         <div class="cpd-actions mb-2">
            <v-btn color="primary" dark href="/admin/cpd-evtprogs">
               <v-icon left small>mdi-calendar-star</v-icon>
               CPD Events & Programs
            </v-btn>
            <v-btn color="primary" dark href="/admin/cpd-evtprog-cat">
               <v-icon left small>mdi-shape-outline</v-icon>
               CPD Categories
            </v-btn>
            <v-btn color="primary" dark href="/admin/cpd-ext-orgs">
               <v-icon left small>mdi-domain</v-icon>
               External Organizers
            </v-btn>
         </div>

         <v-row dense class="mb-3">
            <v-col cols="12" sm="6" lg="3">
               <v-card class="summary-card pending-summary" outlined>
                  <v-card-text>
                     <div class="summary-label">Pending Review</div>
                     <div class="summary-value">{{ filteredPending.length }}</div>
                  </v-card-text>
               </v-card>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
               <v-card class="summary-card approved-summary" outlined>
                  <v-card-text>
                     <div class="summary-label">Approved Records</div>
                     <div class="summary-value">{{ filteredApproved.length }}</div>
                  </v-card-text>
               </v-card>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
               <v-card class="summary-card points-summary" outlined>
                  <v-card-text>
                     <div class="summary-label">Approved Points</div>
                     <div class="summary-value">{{ filteredApprovedPoints }}</div>
                  </v-card-text>
               </v-card>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
               <v-card class="summary-card member-summary" outlined>
                  <v-card-text>
                     <div class="summary-label">Members With CPD</div>
                     <div class="summary-value">{{ filteredUniqueMemberCount }}</div>
                  </v-card-text>
               </v-card>
            </v-col>
         </v-row>

         <v-card class="cpd-card">
            <v-toolbar flat color="white" class="cpd-toolbar">
               <v-toolbar-title>CPD Management</v-toolbar-title>
               <v-divider class="mx-4" inset vertical></v-divider>
               <v-spacer></v-spacer>
               <v-btn color="primary" dark @click="loadAll" :loading="loading">Refresh</v-btn>
            </v-toolbar>

            <div class="cpd-filters">
               <v-text-field
                  v-model="filters.search"
                  label="Search member, IC/passport, programme, code or category"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  dense
                  outlined
                  class="cpd-search"
               ></v-text-field>
               <v-select
                  v-model="filters.year"
                  :items="cpdYears"
                  label="CPD Year"
                  prepend-inner-icon="mdi-calendar"
                  hide-details
                  clearable
                  dense
                  outlined
               ></v-select>
               <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Record Status"
                  prepend-inner-icon="mdi-check-decagram"
                  hide-details
                  clearable
                  dense
                  outlined
               ></v-select>
               <v-btn text color="primary" class="clear-filter-btn" @click="clearFilters">
                  <v-icon left small>mdi-filter-remove</v-icon>
                  Clear
               </v-btn>
            </div>

            <section class="cpd-section">
               <div class="cpd-section-title">
                  Member CPD Registration - Pending Approval
               </div>

               <div class="cpd-table-wrap">
                  <table class="cpd-table">
                     <thead>
                        <tr>
                           <th class="col-no">No.</th>
                           <th>EP Category</th>
                           <th class="col-year">EP Year</th>
                           <th class="col-code">EP Code</th>
                           <th>EP Name</th>
                           <th class="col-date">Submit Date</th>
                           <th class="col-doc">Document</th>
                           <th class="col-actions">Actions</th>
                        </tr>
                     </thead>
                     <tbody>
                        <template v-if="pendingRows.length">
                           <template v-for="group in pendingRows">
                              <tr :key="group.key" class="group-row">
                                 <td colspan="8">
                                    <div class="group-line">
                                       <div>
                                          <v-icon small class="mr-1">mdi-account</v-icon>
                                          {{ group.memberName }} [ {{ group.memberId }}, {{ group.icpassport }} ]
                                          <span class="ml-3">CPD Year: {{ group.cpdyear }}</span>
                                       </div>
                                       <v-chip x-small color="orange darken-2" dark>{{ group.items.length }} pending</v-chip>
                                    </div>
                                 </td>
                              </tr>
                              <tr v-for="item in group.items" :key="item.id">
                                 <td>{{ item.rowNo }}</td>
                                 <td>{{ item.epcategory || '-' }}</td>
                                 <td>{{ item.epyear || item.cpdyear || '-' }}</td>
                                 <td>{{ item.epcode || '-' }}</td>
                                 <td>
                                    <div class="programme-cell">
                                       <span>{{ item.epname || '-' }}</span>
                                       <v-chip
                                          v-if="isUnlistedProgramme(item)"
                                          x-small
                                          color="deep-purple accent-4"
                                          text-color="white"
                                          class="mt-1"
                                       >
                                          <v-icon left x-small>mdi-clipboard-edit-outline</v-icon>
                                          Programme not listed
                                       </v-chip>
                                    </div>
                                 </td>
                                 <td>{{ formatDateTime(item.created_at || item.updated_at || item.epsubmitdate) }}</td>
                                 <td>
                                    <a v-if="programDocumentUrl(item)" :href="programDocumentUrl(item)" target="_blank" class="doc-link">
                                       <v-icon small color="blue">mdi-file-document</v-icon>
                                       View
                                    </a>
                                    <span v-else class="grey--text">No document</span>
                                 </td>
                                 <td class="text-right">
                                    <v-btn x-small color="indigo" dark class="mr-1" @click="openDetail(item, 'pending')">
                                       Details
                                    </v-btn>
                                    <v-btn x-small color="warning" dark class="mr-1" @click="requestMoreInfo(item)">
                                       Request More Info
                                    </v-btn>
                                    <v-btn x-small color="error" dark class="mr-1" @click="confirmReject(item)">
                                       Reject
                                    </v-btn>
                                    <v-btn x-small color="success" dark @click="confirmApprove(item)">
                                       Review & Approve
                                    </v-btn>
                                 </td>
                              </tr>
                           </template>
                        </template>
                        <tr v-else>
                           <td colspan="8" class="empty-cell">
                              <v-icon large color="grey lighten-1">mdi-clipboard-check-outline</v-icon>
                              <div>{{ loading ? 'Loading...' : 'No pending CPD records' }}</div>
                           </td>
                        </tr>
                     </tbody>
                  </table>
               </div>

               <div class="cpd-footer">
                  <div class="cpd-page-control">
                     Rows per page:
                     <v-select
                        v-model="pendingRowsPerPage"
                        :items="rowsPerPageOptions"
                        hide-details
                        dense
                        class="rows-select"
                        @change="pendingPage = 1"
                     ></v-select>
                  </div>
                  <span>{{ pendingRangeText }}</span>
                  <v-btn icon small :disabled="pendingPage <= 1" @click="pendingPage--">
                     <v-icon small>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn icon small :disabled="pendingEnd >= filteredPending.length" @click="pendingPage++">
                     <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
               </div>
            </section>
         </v-card>

         <v-card class="cpd-card mt-4">
            <v-toolbar flat color="white" class="cpd-toolbar">
               <v-toolbar-title>Member CPD Registration - Approved</v-toolbar-title>
               <v-divider class="mx-4" inset vertical></v-divider>
               <v-spacer></v-spacer>
               <v-btn color="blue-grey darken-2" dark class="mr-2" @click="openAdjustmentHistory">
                  <v-icon left small>mdi-history</v-icon>
                  Point Adjustments
               </v-btn>
               <v-btn color="primary" dark class="mr-2" @click="showImportDialog = true">Import From CSV</v-btn>
               <v-btn color="success" dark @click="exportApprovedToExcel">
                  <v-icon left small>mdi-microsoft-excel</v-icon>
                  Export to Excel
               </v-btn>
            </v-toolbar>

            <div class="cpd-table-wrap">
               <table class="cpd-table">
                  <thead>
                     <tr>
                        <th class="col-no">No.</th>
                        <th>EP Category</th>
                        <th class="col-year">EP Year</th>
                        <th class="col-code">EP Code</th>
                        <th>EP Name</th>
                        <th class="col-date">Approved Date</th>
                        <th class="col-points">CPD Points</th>
                        <th class="col-doc">Document</th>
                        <th class="col-actions">Actions</th>
                     </tr>
                  </thead>
                  <tbody>
                     <template v-if="approvedRows.length">
                        <template v-for="group in approvedRows">
                           <tr :key="group.key" class="group-row">
                              <td colspan="9">
                                 <div class="group-line">
                                    <div>
                                       <v-icon small class="mr-1">mdi-account-check</v-icon>
                                       {{ group.memberName }} [ {{ group.memberId }}, {{ group.icpassport }} ]
                                       <span class="ml-3">CPD Year: {{ group.cpdyear }}</span>
                                       <span class="ml-3">CPD Points: {{ group.totalPoints }}</span>
                                    </div>
                                    <div class="group-actions">
                                       <v-chip
                                          v-if="group.adjustmentIn || group.adjustmentOut"
                                          x-small
                                          color="blue-grey darken-2"
                                          dark
                                       >
                                          Adjustment +{{ group.adjustmentIn }} / -{{ group.adjustmentOut }}
                                       </v-chip>
                                       <v-btn
                                          v-if="Number(group.totalPoints || 0) > 12"
                                          x-small
                                          color="deep-purple darken-2"
                                          dark
                                          @click="openTransferDialog(group)"
                                       >
                                          <v-icon left x-small>mdi-swap-horizontal</v-icon>
                                          Transfer Points
                                       </v-btn>
                                       <v-chip v-else x-small outlined color="blue-grey">
                                          No Excess Points
                                       </v-chip>
                                       <v-btn x-small color="primary" dark @click="sendRenewalEmail(group)">
                                          Send Renewal Email
                                       </v-btn>
                                    </div>
                                 </div>
                              </td>
                           </tr>
                           <tr v-for="item in group.items" :key="item.id">
                              <td>{{ item.rowNo }}</td>
                              <td>{{ item.epcategory || '-' }}</td>
                              <td>{{ item.epyear || item.cpdyear || '-' }}</td>
                              <td>{{ item.epcode || '-' }}</td>
                              <td>{{ item.epname || '-' }}</td>
                              <td>{{ formatDateTime(item.approveddate) }}</td>
                              <td>{{ item.cpdpoints || item.eppoints || 0 }}</td>
                              <td>
                                 <a v-if="programDocumentUrl(item)" :href="programDocumentUrl(item)" target="_blank" class="doc-link">
                                    <v-icon small color="blue">mdi-file-document</v-icon>
                                    View
                                 </a>
                                 <span v-else-if="item.docfileexists === false" class="orange--text" title="File was recorded but is missing from server">
                                    <v-icon small color="orange">mdi-alert-circle</v-icon>
                                    Missing
                                 </span>
                                 <span v-else-if="item.docfileexists === false" class="orange--text" title="File was recorded but is missing from server">
                                    <v-icon small color="orange">mdi-alert-circle</v-icon>
                                    Missing
                                 </span>
                                 <span v-else class="grey--text">No document</span>
                              </td>
                              <td class="text-right">
                                 <v-btn x-small color="indigo" dark @click="openDetail(item, 'approved')">
                                    Details
                                 </v-btn>
                              </td>
                           </tr>
                        </template>
                     </template>
                     <tr v-else>
                        <td colspan="9" class="empty-cell">
                           <v-icon large color="grey lighten-1">mdi-certificate-outline</v-icon>
                           <div>{{ loading ? 'Loading...' : 'No approved CPD records' }}</div>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>

            <div class="cpd-footer">
               <div class="cpd-page-control">
                  Rows per page:
                  <v-select
                     v-model="approvedRowsPerPage"
                     :items="rowsPerPageOptions"
                     hide-details
                     dense
                     class="rows-select"
                     @change="approvedPage = 1"
                  ></v-select>
               </div>
               <span>{{ approvedRangeText }}</span>
               <v-btn icon small :disabled="approvedPage <= 1" @click="approvedPage--">
                  <v-icon small>mdi-chevron-left</v-icon>
               </v-btn>
               <v-btn icon small :disabled="approvedEnd >= filteredApproved.length" @click="approvedPage++">
                  <v-icon small>mdi-chevron-right</v-icon>
               </v-btn>
            </div>
         </v-card>

         <v-dialog v-model="showImportDialog" max-width="720px">
            <v-card>
               <v-card-title>Import CPD From CSV</v-card-title>
               <v-card-text>
                  <v-alert dense text type="info">
                     Upload one or more CSV files. Header must follow existing CPD import format.
                  </v-alert>

                  <v-file-input
                     v-model="importForm.csvcats"
                     label="CPD Categories CSV"
                     accept=".csv"
                     show-size
                     prepend-icon="mdi-paperclip"
                  ></v-file-input>

                  <v-file-input
                     v-model="importForm.csveps"
                     label="CPD Events / Programs CSV"
                     accept=".csv"
                     show-size
                     prepend-icon="mdi-paperclip"
                  ></v-file-input>

                  <v-file-input
                     v-model="importForm.csvatts"
                     label="CPD Member Registrations CSV"
                     accept=".csv"
                     show-size
                     prepend-icon="mdi-paperclip"
                  ></v-file-input>
               </v-card-text>

               <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="secondary" text @click="showImportDialog = false">Close</v-btn>
                  <v-btn color="primary" dark :loading="importing" @click="importCsv">Import</v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <v-dialog v-model="detailDialog" max-width="820px">
            <v-card v-if="selectedItem" class="cpd-detail-card">
               <v-card-title class="cpd-detail-title">
                  <div>
                     <div class="text-h6">CPD Registration Details</div>
                     <div class="detail-subtitle">
                        {{ selectedItem.fullname || '-' }} · {{ selectedItem.memberid || selectedItem.icpassport || '-' }}
                     </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn icon @click="detailDialog = false">
                     <v-icon>mdi-close</v-icon>
                  </v-btn>
               </v-card-title>

               <v-card-text class="pt-4">
                  <v-alert
                     v-if="detailMode === 'pending' && isUnlistedProgramme(selectedItem)"
                     dense
                     text
                     type="info"
                     class="mb-4"
                  >
                     This programme was submitted by a member because it was not listed. Review the supporting document, assign an EP code, confirm the CPD points, then approve.
                  </v-alert>
                  <v-row dense>
                     <v-col cols="12" md="6">
                        <div class="detail-box">
                           <div class="detail-label">Member</div>
                           <div class="detail-value">{{ detailValue(selectedItem.fullname) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">Member ID</div>
                           <div class="detail-value">{{ detailValue(selectedItem.memberid) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">IC / Passport</div>
                           <div class="detail-value">{{ detailValue(selectedItem.icpassport) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">CPD Year</div>
                           <div class="detail-value">{{ detailValue(selectedItem.cpdyear) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">EP Year</div>
                           <div class="detail-value">{{ detailValue(selectedItem.epyear || selectedItem.cpdyear) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">CPD Points</div>
                           <div class="detail-value">{{ selectedItem.cpdpoints || selectedItem.eppoints || 0 }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="3">
                        <div class="detail-box">
                           <div class="detail-label">Status</div>
                           <v-chip x-small :color="detailMode === 'approved' ? 'green darken-1' : 'orange darken-2'" dark>
                              {{ detailMode === 'approved' ? 'Approved' : 'Pending Review' }}
                           </v-chip>
                        </div>
                     </v-col>
                     <v-col cols="12" md="4">
                        <div class="detail-box">
                           <div class="detail-label">Category</div>
                           <div class="detail-value">{{ detailValue(selectedItem.epcategory) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="4">
                        <div class="detail-box">
                           <div class="detail-label">EP Code</div>
                           <div class="detail-value">{{ detailValue(selectedItem.epcode) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="4">
                        <div class="detail-box">
                           <div class="detail-label">Organizer</div>
                           <div class="detail-value">{{ detailValue(selectedItem.eporg) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12">
                        <div class="detail-box">
                           <div class="detail-label">Programme Name</div>
                           <div class="detail-value">{{ detailValue(selectedItem.epname) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="6">
                        <div class="detail-box">
                           <div class="detail-label">Submitted Date</div>
                           <div class="detail-value">{{ formatDateTime(selectedItem.created_at || selectedItem.updated_at || selectedItem.epsubmitdate) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" md="6">
                        <div class="detail-box">
                           <div class="detail-label">Approved Date</div>
                           <div class="detail-value">{{ formatDateTime(selectedItem.approveddate) }}</div>
                        </div>
                     </v-col>
                     <v-col cols="12" v-if="programDocumentUrl(selectedItem)">
                        <div class="detail-box">
                           <div class="detail-label">Document / Poster</div>
                           <a :href="programDocumentUrl(selectedItem)" target="_blank" class="doc-link strong-doc">
                              <v-icon small color="blue">mdi-open-in-new</v-icon>
                              Open uploaded document
                           </a>
                        </div>
                     </v-col>
                  </v-row>
               </v-card-text>

               <v-card-actions>
                  <v-btn
                     v-if="programDocumentUrl(selectedItem)"
                     color="secondary"
                     :href="programDocumentUrl(selectedItem)"
                     target="_blank"
                  >
                     <v-icon left small>mdi-file-eye</v-icon>
                     View Document
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn text @click="detailDialog = false">Close</v-btn>
                  <v-btn v-if="detailMode === 'pending'" color="warning" dark class="mr-1" @click="requestMoreInfo(selectedItem)">Request More Info</v-btn>
                  <v-btn v-if="detailMode === 'pending'" color="error" dark class="mr-1" @click="confirmReject(selectedItem)">
                     <v-icon left small>mdi-close-circle</v-icon>
                     Reject
                  </v-btn>
                  <v-btn v-if="detailMode === 'pending'" color="success" dark @click="confirmApprove(selectedItem)">
                     <v-icon left small>mdi-check</v-icon>
                     Review & Approve
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <v-dialog v-model="approvalDialog" max-width="760px">
            <v-card v-if="approvalItem" class="cpd-approval-card">
               <v-card-title class="cpd-detail-title">
                  <div>
                     <div class="text-h6">Review CPD Approval</div>
                     <div class="detail-subtitle">
                        {{ approvalItem.fullname || '-' }} - {{ approvalItem.memberid || approvalItem.icpassport || '-' }}
                     </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip v-if="isUnlistedProgramme(approvalItem)" small color="deep-purple accent-4" text-color="white">
                     Programme not listed
                  </v-chip>
                  <v-btn icon class="ml-2" @click="approvalDialog = false">
                     <v-icon>mdi-close</v-icon>
                  </v-btn>
               </v-card-title>

               <v-card-text class="pt-4">
                  <v-alert dense text type="info" class="mb-4">
                     Approval will save the programme as an approved CPD programme and approve this member's CPD registration.
                  </v-alert>
                  <v-row dense>
                     <v-col cols="12" md="3">
                        <v-text-field
                           v-model="approvalForm.epcode"
                           label="EP Code"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="approvalErrors.epcode"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="3">
                        <v-text-field
                           v-model="approvalForm.cpdpoints"
                           label="CPD Points"
                           type="number"
                           min="0"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="approvalErrors.cpdpoints"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="3">
                        <v-text-field
                           v-model="approvalForm.epcatid"
                           label="Category ID"
                           outlined
                           dense
                           readonly
                           hide-details="auto"
                           :error-messages="approvalErrors.epcatid"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="3">
                        <v-text-field
                           v-model="approvalForm.cpdyear"
                           label="CPD Year"
                           type="number"
                           min="2000"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="approvalErrors.cpdyear"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12">
                        <v-text-field
                           v-model="approvalForm.epname"
                           label="Programme Name"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="approvalErrors.epname"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="approvalForm.eporg"
                           label="Organizer"
                           outlined
                           dense
                           hide-details="auto"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="approvalForm.epemail"
                           label="Organizer Email"
                           outlined
                           dense
                           hide-details="auto"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="approvalForm.epcontactno"
                           label="Organizer Contact No."
                           outlined
                           dense
                           hide-details="auto"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-select
                           v-model="approvalForm.epcatid"
                           :items="cpdCategories"
                           item-text="category"
                           item-value="id"
                           label="Selected Category"
                           outlined
                           dense
                           hide-details="auto"
                           :loading="loadingCategories"
                           :error-messages="approvalErrors.epcatid"
                           no-data-text="No CPD category available"
                        ></v-select>
                     </v-col>
                     <v-col cols="12" v-if="programDocumentUrl(approvalItem)">
                        <a :href="programDocumentUrl(approvalItem)" target="_blank" class="doc-link strong-doc">
                           <v-icon small color="blue">mdi-open-in-new</v-icon>
                           Open supporting document
                        </a>
                     </v-col>
                  </v-row>
               </v-card-text>

               <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="approvalDialog = false">Cancel</v-btn>
                  <v-btn color="success" dark :loading="approving" @click="submitApproval">
                     <v-icon left small>mdi-check-decagram</v-icon>
                     Approve CPD
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <v-dialog v-model="rejectDialog" max-width="560px">
            <v-card v-if="rejectItem">
               <v-card-title class="text-h6">
                  Reject CPD Registration?
               </v-card-title>
               <v-card-text>
                  <v-alert dense text type="warning" class="mb-4">
                     This will remove the pending CPD registration from the member's record. Use this only when the submission is duplicated, incorrect, or not related to the member.
                  </v-alert>
                  <div class="detail-box">
                     <div class="detail-label">Member</div>
                     <div class="detail-value">{{ detailValue(rejectItem.fullname) }}</div>
                  </div>
                  <div class="detail-box mt-2">
                     <div class="detail-label">Programme</div>
                     <div class="detail-value">{{ detailValue(rejectItem.epname) }}</div>
                  </div>
               </v-card-text>
               <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="rejectDialog = false">Cancel</v-btn>
                  <v-btn color="error" dark :loading="rejecting" @click="submitReject">
                     <v-icon left small>mdi-close-circle</v-icon>
                     Reject Registration
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <v-dialog v-model="transferDialog" max-width="640px" persistent>
            <v-card class="transfer-card">
               <v-card-title class="transfer-title">
                  <div>
                     <div class="transfer-heading">Transfer CPD Points</div>
                     <div class="transfer-member">
                        {{ transferForm.fullname }} [{{ transferForm.memberid }}]
                     </div>
                  </div>
                  <v-btn icon aria-label="Close" :disabled="transferSubmitting" @click="transferDialog = false">
                     <v-icon>mdi-close</v-icon>
                  </v-btn>
               </v-card-title>

               <v-divider></v-divider>

               <v-card-text class="pt-5">
                  <v-alert dense text type="info" class="mb-5">
                     Only points above the required 12 CPD points can be transferred. Every transfer is recorded for audit purposes.
                  </v-alert>

                  <div class="transfer-summary mb-5">
                     <div>
                        <span>Source year total</span>
                        <strong>{{ transferSourceTotal }}</strong>
                     </div>
                     <div>
                        <span>Required balance</span>
                        <strong>12</strong>
                     </div>
                     <div>
                        <span>Available to transfer</span>
                        <strong>{{ transferAvailablePoints }}</strong>
                     </div>
                  </div>

                  <v-row>
                     <v-col cols="12" sm="6">
                        <v-text-field
                           v-model.number="transferForm.source_year"
                           label="Source Year"
                           outlined
                           dense
                           readonly
                           hide-details
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12" sm="6">
                        <v-text-field
                           v-model.number="transferForm.target_year"
                           label="Target Year"
                           type="number"
                           outlined
                           dense
                           :error-messages="transferErrors.target_year"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12">
                        <v-text-field
                           v-model.number="transferForm.points"
                           label="Points to Transfer"
                           type="number"
                           min="1"
                           :max="transferAvailablePoints"
                           outlined
                           dense
                           :error-messages="transferErrors.points"
                        ></v-text-field>
                     </v-col>
                     <v-col cols="12">
                        <v-textarea
                           v-model="transferForm.remarks"
                           label="Reason / Remarks"
                           outlined
                           rows="3"
                           counter="1000"
                           :error-messages="transferErrors.remarks"
                        ></v-textarea>
                     </v-col>
                  </v-row>
               </v-card-text>

               <v-divider></v-divider>

               <v-card-actions class="px-6 py-4">
                  <v-spacer></v-spacer>
                  <v-btn text :disabled="transferSubmitting" @click="transferDialog = false">Cancel</v-btn>
                  <v-btn
                     v-if="transferAvailablePoints > 0"
                     color="deep-purple darken-2"
                     dark
                     :loading="transferSubmitting"
                     @click="submitTransfer"
                  >
                     <v-icon left small>mdi-content-save</v-icon>
                     Save Transfer
                  </v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>

         <v-dialog v-model="adjustmentHistoryDialog" max-width="980px">
            <v-card class="transfer-card">
               <v-card-title class="transfer-title">
                  <div>
                     <div class="transfer-heading">CPD Point Adjustment History</div>
                     <div class="transfer-member">Administrative transfers between CPD years</div>
                  </div>
                  <v-btn icon aria-label="Close" @click="adjustmentHistoryDialog = false">
                     <v-icon>mdi-close</v-icon>
                  </v-btn>
               </v-card-title>
               <v-divider></v-divider>
               <v-card-text class="pa-0">
                  <div class="cpd-table-wrap">
                     <table class="cpd-table adjustment-table">
                        <thead>
                           <tr>
                              <th>Member</th>
                              <th>From</th>
                              <th>To</th>
                              <th>Points</th>
                              <th>Remarks</th>
                              <th>Updated By</th>
                              <th>Date</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr v-for="item in adjustmentItems" :key="item.id">
                              <td>
                                 <strong>{{ item.fullname || '-' }}</strong>
                                 <div class="transfer-member">{{ item.memberid || '-' }}</div>
                              </td>
                              <td>{{ item.source_year }}</td>
                              <td>{{ item.target_year }}</td>
                              <td><strong>{{ item.points }}</strong></td>
                              <td>{{ item.remarks || '-' }}</td>
                              <td>{{ item.admin_name || '-' }}</td>
                              <td>{{ formatDateTime(item.created_at) }}</td>
                           </tr>
                           <tr v-if="!adjustmentLoading && !adjustmentItems.length">
                              <td colspan="7" class="empty-cell">No CPD point adjustments recorded.</td>
                           </tr>
                           <tr v-if="adjustmentLoading">
                              <td colspan="7" class="empty-cell">Loading adjustment history...</td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </v-card-text>
            </v-card>
         </v-dialog>

         <v-snackbar v-model="snackbar" :color="snackbarColor">{{ snackbarText }}</v-snackbar>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOACpd',
   components: { DashboardLayout, BaseBreadcrumb },
   data() {
      return {
         page: { title: 'Manage CPD' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'CPD', disabled: true },
         ],
         loading: false,
         loadingCategories: false,
         pendingItems: [],
         approvedItems: [],
         cpdCategories: [],
         rowsPerPageOptions: [10, 25, 50, 100],
         pendingRowsPerPage: 10,
         approvedRowsPerPage: 10,
         pendingPage: 1,
         approvedPage: 1,
         showImportDialog: false,
         detailDialog: false,
         approvalDialog: false,
         rejectDialog: false,
         transferDialog: false,
         adjustmentHistoryDialog: false,
         selectedItem: null,
         approvalItem: null,
         rejectItem: null,
         detailMode: 'pending',
         importing: false,
         approving: false,
         rejecting: false,
         transferSubmitting: false,
         adjustmentLoading: false,
         transferGroup: null,
         adjustmentItems: [],
         importForm: {
            csvcats: null,
            csveps: null,
            csvatts: null,
         },
         approvalForm: {
            epcode: '',
            epcatid: '',
            cpdyear: '',
            cpdpoints: 0,
            epname: '',
            eporg: '',
            epcontactno: '',
            epemail: '',
         },
         approvalErrors: {},
         transferForm: {
            userid: '',
            fullname: '',
            memberid: '',
            source_year: '',
            target_year: '',
            points: 1,
            remarks: '',
         },
         transferErrors: {},
         filters: {
            search: '',
            year: '',
            status: '',
         },
         statusOptions: [
            { text: 'Pending Review', value: 'pending' },
            { text: 'Approved', value: 'approved' },
         ],
         snackbar: false,
         snackbarColor: 'info',
         snackbarText: '',
      }
   },
   computed: {
      filteredPending() {
         if (this.filters.status === 'approved') return []
         return this.filterItems(this.pendingItems)
      },
      filteredApproved() {
         if (this.filters.status === 'pending') return []
         return this.filterItems(this.approvedItems)
      },
      cpdYears() {
         return [...new Set([...this.pendingItems, ...this.approvedItems].map(item => item.cpdyear).filter(Boolean))]
            .sort((a, b) => Number(b) - Number(a))
      },
      filteredApprovedPoints() {
         return this.filteredApproved.reduce((total, item) => total + parseInt(item.cpdpoints || item.eppoints || 0), 0)
      },
      filteredUniqueMemberCount() {
         return new Set([...this.filteredPending, ...this.filteredApproved].map(item => item.userid || item.memberid || item.icpassport).filter(Boolean)).size
      },
      pendingStart() {
         return (this.pendingPage - 1) * this.pendingRowsPerPage
      },
      pendingEnd() {
         return Math.min(this.pendingStart + this.pendingRowsPerPage, this.filteredPending.length)
      },
      approvedStart() {
         return (this.approvedPage - 1) * this.approvedRowsPerPage
      },
      approvedEnd() {
         return Math.min(this.approvedStart + this.approvedRowsPerPage, this.filteredApproved.length)
      },
      pagedPending() {
         return this.filteredPending.slice(this.pendingStart, this.pendingEnd)
      },
      pagedApproved() {
         return this.filteredApproved.slice(this.approvedStart, this.approvedEnd)
      },
      pendingRows() {
         return this.groupRows(this.pagedPending, this.pendingStart)
      },
      approvedRows() {
         return this.groupRows(this.pagedApproved, this.approvedStart)
      },
      pendingRangeText() {
         return this.rangeText(this.pendingStart, this.pendingEnd, this.filteredPending.length)
      },
      approvedRangeText() {
         return this.rangeText(this.approvedStart, this.approvedEnd, this.filteredApproved.length)
      },
      transferSourceTotal() {
         return this.transferGroup ? Number(this.transferGroup.totalPoints || 0) : 0
      },
      transferAvailablePoints() {
         return Math.max(0, this.transferSourceTotal - 12)
      },
   },
   watch: {
      filters: {
         deep: true,
         handler() {
            this.pendingPage = 1
            this.approvedPage = 1
         },
      },
   },
   mounted() {
      this.loadAll()
      this.loadCategories()
   },
   methods: {
      loadAll() {
         this.loading = true
         Promise.allSettled([
            axios.get('/admin/cpd/list-pending'),
            axios.get('/admin/cpd/list-approved'),
         ]).then(([pending, approved]) => {
            this.pendingItems = pending.status === 'fulfilled' ? this.responseItems(pending.value) : []
            this.approvedItems = approved.status === 'fulfilled' ? this.responseItems(approved.value) : []

            if (pending.status === 'rejected' || approved.status === 'rejected') {
               this.notify(false, this.errorText(pending.reason || approved.reason))
            }
         }).finally(() => {
            this.loading = false
         })
      },
      responseItems(resp) {
         return resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
      },
      loadCategories() {
         this.loadingCategories = true
         axios.get('/admin/cpd-evtprg-cat/ddllist')
            .then((resp) => {
               this.cpdCategories = this.responseItems(resp)
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.loadingCategories = false
            })
      },
      filterItems(items) {
         return items.filter((item) => {
            return this.matchesSmartSearch(item, this.filters.search)
               && this.includes(item.cpdyear, this.filters.year)
         })
      },
      matchesSmartSearch(item, needle) {
         if (!needle) return true
         const haystack = [
            item.fullname,
            item.memberid,
            item.icpassport,
            item.cpdyear,
            item.epcategory,
            item.epyear,
            item.epcode,
            item.epname,
            item.eporg,
            item.epemail,
         ].join(' ').toLowerCase()
         return haystack.indexOf(String(needle).toLowerCase()) !== -1
      },
      includes(value, needle) {
         if (!needle) return true
         return String(value || '').toLowerCase().indexOf(String(needle).toLowerCase()) !== -1
      },
      clearFilters() {
         this.filters = {
            search: '',
            year: '',
            status: '',
         }
      },
      groupRows(items, startIndex) {
         const groups = []
         const byKey = {}
         let rowNo = startIndex + 1
         items.forEach((raw) => {
            const item = Object.assign({}, raw, { rowNo: rowNo++ })
            const key = [
               item.userid || item.memberid || item.icpassport || 'member',
               item.cpdyear || 'year',
            ].join('-')
            if (!byKey[key]) {
               byKey[key] = {
                  key,
                  memberName: item.fullname || '-',
                  memberId: item.memberid || '-',
                  userid: item.userid || null,
                  icpassport: item.icpassport || '-',
                  cpdyear: item.cpdyear || '-',
                  totalPoints: 0,
                  basePoints: Number(item.cpd_base_points || 0),
                  adjustmentIn: Number(item.cpd_adjustment_in || 0),
                  adjustmentOut: Number(item.cpd_adjustment_out || 0),
                  items: [],
               }
               groups.push(byKey[key])
            }
            if (Object.prototype.hasOwnProperty.call(item, 'cpd_total_points')) {
               byKey[key].totalPoints = Number(item.cpd_total_points || 0)
            } else {
               byKey[key].totalPoints += parseInt(item.cpdpoints || item.eppoints || 0)
            }
            byKey[key].items.push(item)
         })
         return groups
      },
      rangeText(start, end, total) {
         if (!total) {
            return '0-0 of 0'
         }

         return `${start + 1}-${end} of ${total}`
      },
      isUnlistedProgramme(item) {
         if (!item) return false
         return String(item.program_status || '') === '02' || String(item.program_isapproved) === '0'
      },
      openApprovalDialog(item) {
         if (!item) return
         if (!this.cpdCategories.length) {
            this.loadCategories()
         }
         this.approvalItem = item
         this.approvalErrors = {}
         this.approvalForm = {
            epcode: item.epcode || '',
            epcatid: item.epcatid || '',
            cpdyear: item.cpdyear || item.epyear || new Date().getFullYear(),
            cpdpoints: item.eppoints || item.cpdpoints || 0,
            epname: item.epname || '',
            eporg: item.eporg || '',
            epcontactno: item.epcontactno || '',
            epemail: item.epemail || '',
         }
         this.approvalDialog = true
      },
      validateApprovalForm() {
         const errors = {}
         if (!String(this.approvalForm.epcode || '').trim()) {
            errors.epcode = ['EP Code is required.']
         }
         if (!String(this.approvalForm.epcatid || '').trim()) {
            errors.epcatid = ['Category is required.']
         }
         if (!String(this.approvalForm.epname || '').trim()) {
            errors.epname = ['Programme Name is required.']
         }
         const cpdyear = Number(this.approvalForm.cpdyear)
         if (!Number.isInteger(cpdyear) || cpdyear < 2000) {
            errors.cpdyear = ['CPD Year is required.']
         }
         const points = Number(this.approvalForm.cpdpoints)
         if (!Number.isFinite(points) || points < 0) {
            errors.cpdpoints = ['CPD Points must be 0 or higher.']
         }
         this.approvalErrors = errors
         return Object.keys(errors).length === 0
      },
      submitApproval() {
         if (!this.approvalItem || !this.validateApprovalForm()) return
         this.approving = true
         this.approveItem(this.approvalItem, this.approvalForm)
      },
      approveItem(item, values) {
         const approval = values || item
         const data = new FormData()
         data.append('id', item.id)
         data.append('epcode', approval.epcode || '')
         data.append('epcatid', approval.epcatid || '')
         data.append('cpdyear', approval.cpdyear || '')
         data.append('cpdpoints', approval.cpdpoints || approval.eppoints || 0)
         data.append('epname', approval.epname || '')
         data.append('eporg', approval.eporg || '')
         data.append('epcontactno', approval.epcontactno || '')
         data.append('epemail', approval.epemail || '')
         axios.post('/admin/cpd/registration/approve', data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? 'CPD registration approved.' : (resp.data.error || 'Unable to approve CPD registration.'))
               if (ok) {
                  this.approvalDialog = false
                  this.detailDialog = false
                  this.loadAll()
               }
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.approving = false
            })
      },
      confirmApprove(item) {
         if (!item) return
         this.openApprovalDialog(item)
      },
      confirmReject(item) {
         if (!item) return
         this.rejectItem = item
         this.rejectDialog = true
      },
      submitReject() {
         if (!this.rejectItem) return
         this.rejecting = true

         const data = new FormData()
         data.append('id', this.rejectItem.id)

         axios.post('/admin/cpd/delete', data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? (resp.data.message || 'CPD registration rejected.') : (resp.data.error || 'Unable to reject CPD registration.'))
               if (ok) {
                  this.rejectDialog = false
                  this.detailDialog = false
                  this.rejectItem = null
                  this.loadAll()
               }
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.rejecting = false
            })
      },
      requestMoreInfo(item) {
         if (!item) return
         const message = prompt(
            'Enter remarks for ' + (item.fullname || 'this member') + ':',
            item.moreinfo_message || 'Please provide additional information or a clearer supporting document for this CPD registration.'
         )
         if (message === null) {
            return
         }

         const data = new FormData()
         data.append('id', item.id)
         data.append('message', message)
         axios.post('/admin/cpd/moreinfo', data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? (resp.data.message || 'Request for more info sent to member.') : (resp.data.error || 'Unable to send request.'))
               this.detailDialog = false
               this.loadAll()
            })
            .catch((error) => this.notify(false, this.errorText(error)))
      },
      openDetail(item, mode) {
         this.selectedItem = item
         this.detailMode = mode || (item && item.isapproved ? 'approved' : 'pending')
         this.detailDialog = true
      },
      detailValue(value) {
         return value === null || value === undefined || value === '' ? '-' : value
      },
      programDocumentUrl(item) {
         if (!item) return ''
         if (item.docfileexists === false) return ''
         const path = item.supportingdoc || item.epposter || item.document || item.filepath || ''
         if (!path) return ''
         if (/^https?:\/\//i.test(path)) return path
         return '/' + String(path).replace(/^\/+/, '')
      },
      openAdjustmentHistory() {
         this.adjustmentHistoryDialog = true
         this.adjustmentLoading = true
         axios.get('/admin/cpd/adjustments')
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.adjustmentItems = ok ? (resp.data.data || []) : []
               if (!ok) {
                  this.notify(false, resp.data.error || 'Unable to load CPD point adjustment history.')
               }
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.adjustmentLoading = false
            })
      },
      openTransferDialog(group) {
         if (!group || !group.userid || !group.cpdyear) {
            this.notify(false, 'Member or CPD year is missing.')
            return
         }

         const availablePoints = Math.max(0, Number(group.totalPoints || 0) - 12)
         if (availablePoints < 1) {
            this.notify(false, 'No excess CPD points are available. The source year must retain at least 12 points.')
            return
         }

         this.transferGroup = group
         this.transferErrors = {}
         this.transferForm = {
            userid: group.userid,
            fullname: group.memberName || '-',
            memberid: group.memberId || '-',
            source_year: Number(group.cpdyear),
            target_year: Number(group.cpdyear) + 1,
            points: 1,
            remarks: '',
         }
         this.transferDialog = true
      },
      validateTransferForm() {
         const errors = {}
         const sourceYear = Number(this.transferForm.source_year)
         const targetYear = Number(this.transferForm.target_year)
         const points = Number(this.transferForm.points)

         if (!Number.isInteger(targetYear) || targetYear < 2000) {
            errors.target_year = ['Enter a valid target year.']
         } else if (targetYear === sourceYear) {
            errors.target_year = ['Target year must be different from the source year.']
         } else if (targetYear > sourceYear && targetYear !== sourceYear + 1) {
            errors.target_year = ['Forward transfer is limited to the following year.']
         }

         if (!Number.isInteger(points) || points < 1) {
            errors.points = ['Enter at least 1 CPD point.']
         } else if (points > this.transferAvailablePoints) {
            errors.points = [`Only ${this.transferAvailablePoints} excess CPD point(s) are available.`]
         }

         if (!String(this.transferForm.remarks || '').trim()) {
            errors.remarks = ['Reason or remarks are required.']
         }

         this.transferErrors = errors
         return Object.keys(errors).length === 0
      },
      submitTransfer() {
         if (!this.validateTransferForm()) return

         this.transferSubmitting = true
         axios.post('/admin/cpd/points/transfer', {
            userid: this.transferForm.userid,
            source_year: Number(this.transferForm.source_year),
            target_year: Number(this.transferForm.target_year),
            points: Number(this.transferForm.points),
            remarks: String(this.transferForm.remarks || '').trim(),
         }).then((resp) => {
            const ok = resp.data && resp.data.status === 'OK'
            this.notify(ok, ok ? (resp.data.message || 'CPD points transferred successfully.') : (resp.data.error || 'Unable to transfer CPD points.'))
            if (ok) {
               this.transferDialog = false
               this.transferGroup = null
               this.loadAll()
            }
         }).catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.transferSubmitting = false
            })
      },
      sendRenewalEmail(group) {
         if (!group.userid || !group.cpdyear) {
            this.notify(false, 'Unable to send renewal email. Member or CPD year is missing.')
            return
         }

         const data = new FormData()
         data.append('userid', group.userid)
         data.append('cpdyear', group.cpdyear)

         axios.post('/admin/cpd/emailuser', data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? 'Renewal email sent.' : (resp.data.error || 'Unable to send renewal email.'))
            })
            .catch((error) => this.notify(false, this.errorText(error)))
      },
      importCsv() {
         if (!this.importForm.csvcats && !this.importForm.csveps && !this.importForm.csvatts) {
            this.notify(false, 'Please choose at least one CSV file to import.')
            return
         }

         this.importing = true

         const data = new FormData()
         if (this.importForm.csvcats) data.append('csvcats', this.importForm.csvcats)
         if (this.importForm.csveps) data.append('csveps', this.importForm.csveps)
         if (this.importForm.csvatts) data.append('csvatts', this.importForm.csvatts)

         axios.post('/admin/cpd/importcsv', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
         }).then((resp) => {
            const ok = resp.data && resp.data.status === 'OK'
            this.notify(ok, ok ? (resp.data.message || 'CSV imported successfully.') : (resp.data.error || 'Unable to import CSV.'))
            if (ok) {
               this.showImportDialog = false
               this.importForm.csvcats = null
               this.importForm.csveps = null
               this.importForm.csvatts = null
               this.loadAll()
            }
         }).catch((error) => {
            this.notify(false, this.errorText(error))
         }).finally(() => {
            this.importing = false
         })
      },
      exportApprovedToExcel() {
         if (!this.filteredApproved.length) {
            this.notify(false, 'No approved CPD records to export.')
            return
         }

         const rows = this.filteredApproved.map((item, index) => [
            index + 1,
            item.fullname || '',
            item.memberid || '',
            item.icpassport || '',
            item.cpdyear || '',
            item.epcategory || '',
            item.epyear || item.cpdyear || '',
            item.epcode || '',
            item.epname || '',
            item.cpdpoints || item.eppoints || 0,
            this.formatDateTime(item.approveddate),
         ])

         const htmlRows = [
            ['No.', 'Member', 'Member ID', 'IC/Passport', 'CPD Year', 'EP Category', 'EP Year', 'EP Code', 'EP Name', 'CPD Points', 'Approved Date'],
            ...rows,
         ].map(row => `<tr>${row.map(cell => `<td>${this.escapeHtml(cell)}</td>`).join('')}</tr>`).join('')

         const html = `<html><head><meta charset="UTF-8"></head><body><table>${htmlRows}</table></body></html>`
         const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' })
         const link = document.createElement('a')
         link.href = URL.createObjectURL(blob)
         link.download = `approved-cpd-${this.$moment().format('YYYYMMDD-HHmmss')}.xls`
         document.body.appendChild(link)
         link.click()
         document.body.removeChild(link)
         URL.revokeObjectURL(link.href)
      },
      escapeHtml(value) {
         return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
      },
      notify(ok, text) {
         this.snackbarColor = ok ? 'success' : 'error'
         this.snackbarText = text
         this.snackbar = true
      },
      errorText(error) {
         return error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Unable to complete request.'
      },
      formatDateTime(value) {
         if (!value) return '-'
         return this.$moment(value).format('YYYY-MM-DD HH:mm:ss')
      },
   },
}
</script>

<style scoped>
.cpd-actions {
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
}

.cpd-actions .v-btn {
   letter-spacing: 1px;
   font-size: 12px;
}

.summary-card {
   border-left: 4px solid #90a4ae !important;
   border-radius: 8px;
}

.pending-summary {
   border-left-color: #ef6c00 !important;
}

.approved-summary {
   border-left-color: #2e7d32 !important;
}

.points-summary {
   border-left-color: #1976d2 !important;
}

.member-summary {
   border-left-color: #673ab7 !important;
}

.summary-label {
   color: #6b7280;
   font-size: 12px;
   font-weight: 700;
   letter-spacing: .4px;
   text-transform: uppercase;
}

.summary-value {
   color: #111827;
   font-size: 30px;
   font-weight: 800;
   line-height: 1.1;
   margin-top: 6px;
}

.cpd-card {
   border-radius: 8px;
   overflow: hidden;
}

.cpd-toolbar {
   min-height: 56px;
}

.cpd-card ::v-deep .v-toolbar__title {
   font-size: 18px;
   font-weight: 500;
}

.cpd-card ::v-deep .v-tab {
   font-size: 13px;
   letter-spacing: 1px;
}

.cpd-section-title {
   padding: 10px 16px;
   color: #111827;
   font-size: 16px;
   font-weight: 500;
   border-top: 1px solid #d7dbe2;
   border-bottom: 1px solid #d7dbe2;
}

.cpd-filters {
   display: grid;
   grid-template-columns: minmax(320px, 1fr) 170px 190px auto;
   gap: 12px;
   padding: 16px;
   align-items: center;
   background: #f8fafc;
   border-top: 1px solid #edf2f7;
   border-bottom: 1px solid #edf2f7;
}

.clear-filter-btn {
   justify-self: start;
}

.cpd-table-wrap {
   overflow-x: auto;
}

.cpd-table {
   width: 100%;
   min-width: 980px;
   border-collapse: collapse;
}

.cpd-table th {
   height: 40px;
   padding: 8px 16px;
   border-bottom: 1px solid #d7dbe2;
   color: #263238;
   font-size: 12px;
   font-weight: 700;
   text-align: left;
   vertical-align: middle;
}

.cpd-table td {
   padding: 8px 16px;
   border-bottom: 1px solid #d7dbe2;
   color: #111827;
   font-size: 12px;
   line-height: 1.35;
   vertical-align: middle;
}

.group-row td {
   background: #eef4ff;
   color: #111827;
   font-weight: 700;
}

.group-line {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
}

.group-actions {
   align-items: center;
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
   justify-content: flex-end;
}

.transfer-card {
   border-radius: 8px;
   overflow: hidden;
}

.transfer-title {
   align-items: flex-start;
   display: flex;
   justify-content: space-between;
   padding: 18px 24px;
}

.transfer-heading {
   color: #111827;
   font-size: 19px;
   font-weight: 700;
}

.transfer-member {
   color: #64748b;
   font-size: 12px;
   font-weight: 500;
   margin-top: 3px;
}

.transfer-summary {
   background: #f8fafc;
   border: 1px solid #dbe4f0;
   border-radius: 6px;
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   overflow: hidden;
}

.transfer-summary > div {
   display: flex;
   flex-direction: column;
   gap: 4px;
   padding: 13px 16px;
}

.transfer-summary > div + div {
   border-left: 1px solid #dbe4f0;
}

.transfer-summary span {
   color: #64748b;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
}

.transfer-summary strong {
   color: #111827;
   font-size: 20px;
}

.col-no {
   width: 58px;
}

.col-year {
   width: 90px;
}

.col-code {
   width: 110px;
}

.col-date {
   width: 160px;
}

.col-points {
   width: 90px;
}

.col-doc {
   width: 130px;
}

.col-actions {
   width: 180px;
   text-align: right !important;
}

.empty-cell {
   height: 110px;
   color: #7b8794 !important;
   text-align: center;
}

.doc-link {
   align-items: center;
   color: #1976d2;
   display: inline-flex;
   gap: 4px;
   font-weight: 600;
   text-decoration: none;
}

.programme-cell {
   align-items: flex-start;
   display: flex;
   flex-direction: column;
}

.strong-doc {
   font-size: 14px;
}

.cpd-approval-card {
   border-radius: 8px;
}

.approval-meta-box {
   background: #f8fafc;
   border: 1px solid #dbe4f0;
   border-radius: 6px;
   min-height: 58px;
   padding: 10px 12px;
}

.cpd-detail-card {
   border-radius: 8px;
}

.cpd-detail-title {
   border-bottom: 1px solid #e5e7eb;
}

.detail-subtitle {
   color: #6b7280;
   font-size: 12px;
   font-weight: 400;
   margin-top: 2px;
}

.detail-box {
   background: #f9fafb;
   border: 1px solid #e5e7eb;
   border-radius: 6px;
   min-height: 66px;
   padding: 10px 12px;
}

.detail-label {
   color: #6b7280;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: .4px;
   margin-bottom: 5px;
   text-transform: uppercase;
}

.detail-value {
   color: #111827;
   font-size: 14px;
   font-weight: 600;
   line-height: 1.4;
   overflow-wrap: anywhere;
}

.cpd-footer {
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 12px;
   padding: 12px 16px;
   color: #455a64;
   font-size: 12px;
}

.cpd-page-control {
   display: flex;
   align-items: center;
   gap: 8px;
}

.rows-select {
   max-width: 72px;
}

.cpd-footer ::v-deep .v-input {
   font-size: 12px;
}

@media (max-width: 960px) {
   .cpd-filters {
      grid-template-columns: 1fr;
   }

   .group-line {
      align-items: flex-start;
      flex-direction: column;
   }

   .group-actions {
      justify-content: flex-start;
   }

   .transfer-summary {
      grid-template-columns: 1fr;
   }

   .transfer-summary > div + div {
      border-left: 0;
      border-top: 1px solid #dbe4f0;
   }
}
</style>
